'use client'

import type { VariantProps } from 'class-variance-authority'
import * as React from 'react'
import {
  createContext,
  PropsWithChildren,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'

import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Button,
  buttonVariants,
} from '@/components/ui/button'

type ConfirmDialogOptions = {
  title?: string
  description?: ReactNode
  onConfirm?: () => Promise<void> | void
  onCancel?: () => void
  confirmText?: string
  cancelText?: string
  confirmVariant?: VariantProps<typeof buttonVariants>['variant']
  cancelVariant?: VariantProps<typeof buttonVariants>['variant']
}

type ConfirmDialogContextType = {
  confirm: (options: ConfirmDialogOptions) => void
}

const ConfirmDialogContext = createContext<ConfirmDialogContextType | undefined>(undefined)

export function ConfirmDialogProvider({ children }: PropsWithChildren) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [{
    title = 'Please confirm',
    description,
    onConfirm,
    onCancel,
    cancelText = 'Cancel',
    confirmText = 'Confirm',
    confirmVariant = 'primary',
    cancelVariant = 'outline',
  }, setOptions] = useState<ConfirmDialogOptions>({})

  const value = useMemo(() => ({
    confirm: (opts: ConfirmDialogOptions) => {
      setOptions(opts)
      setOpen(true)
    },
  }), [])

  const handleConfirm = useCallback(async () => {
    setLoading(true)
    try {
      await onConfirm?.()
      setOpen(false)
    } catch (error) {}
    setLoading(false)
  }, [onConfirm])

  const handleCancel = useCallback(() => {
    setOpen(false)
    onCancel?.()
  }, [onCancel])

  return (
    <ConfirmDialogContext.Provider value={value}>
      {children}
      <AlertDialog open={open}>
        <AlertDialogContent>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
          <div className="flex justify-end gap-2 mt-4">
            <Button onClick={handleCancel} disabled={loading} variant={cancelVariant}>
              {cancelText}
            </Button>
            <Button isLoading={loading} onClick={handleConfirm} disabled={loading} variant={confirmVariant}>
              {confirmText}
            </Button>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmDialogContext.Provider>
  )
}

export function useConfirmDialog() {
  const ctx = useContext(ConfirmDialogContext)
  if (!ctx) throw new Error('useConfirmDialog must be used within ConfirmDialogContext')
  return ctx.confirm
}
